// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

import { App } from "../../../app/App.js"
import { cloneTemplate, getMembraneBreakdownImageUrl, renderLucideIcons } from "../../../utils/index.js"
import { StyledComponent } from "../../base/StyledComponent.js"
import { SteppedContainer } from "../../containers/SteppedContainer.js"

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import {
    EpdmCalculator,
    TrimColor,
    TrimType,
    MEMBRANES,
    Deck,
    toKebabCase,
    EDGE_TYPES,
    PRODUCT_DISPLAY_CATEGORIES,
    SeamDirection,
} from "../../../../../package/index.js"
import { CollapsibleContainer } from "../../containers/CollapsibleContainer.js"
import { deckEditor } from "../../../layouts/index.js"
import { ListSelect } from "../../elements/ListSelect.js"

// @@body
class QuoteUpgradeCard extends HTMLElement {
    constructor() {
        super()
    }

    async connectedCallback() {
        const card = cloneTemplate("quote-upgrade")

        // 
        card.querySelector("[data-accessor='membrane-guide-button']")
            ?.addEventListener("click", () => {
                this.renderGuideModal("membrane-guide")
            })
        card.querySelector("[data-accessor='membrane-guide-link']")
            ?.addEventListener("click", () => {
                this.renderGuideModal("membrane-guide")
            })

        card.querySelector("[data-accessor='trim-guide-button']")
            ?.addEventListener("click", () => {
                this.renderGuideModal("trim-guide")
            })
        card.querySelector("[data-accessor='trim-guide-link']")
            ?.addEventListener("click", () => {
                this.renderGuideModal("trim-guide")
            })

        // Remove PVC trims upgrade section if no PVC trims are required on the
        // quote.
        const pvcTrimsQuoted = this.#deck.edges.array.some(edge => {
            const edgeType = this.#deck.getEdgeType(edge)

            return edgeType === EDGE_TYPES.raisedKerb ||
                edgeType === EDGE_TYPES.gutterDrip
        })
        if (!pvcTrimsQuoted) {
            card.querySelector("[data-accessor='trim-upgrade']")?.remove()
        }

        const selector = card
            .querySelector("[data-accessor='seam-direction-selector']")
        selector?.addEventListener("change", () => {
            const seamDirection = this.#calculator.config.seamDirection
            if (selector.value !== seamDirection) {
                this.#calculator.config.seamDirection = selector.value 
                App.dispatchStateUpdated()
            }
        })

        ListSelect.preConnectValue(
            selector,
            this.#calculator.config.seamDirection
        )

        // 
        this.append(card)
        renderLucideIcons()

        // 
        this.renderState()
        App.addStateListener(() => { this.renderState() })
    }

    renderGuideModal(id) {
        const modal = SteppedContainer.modalContainer()
        const guide = cloneTemplate(id)

        // 
        guide.querySelector("button.primary")
            ?.addEventListener("click", () => { App.container.unloadModal() })

        modal.append(guide)
        App.container.loadModal(modal)
    }

    /**
     * Remove app state listener.
     */
    disconnectedCallback() {
        App.removeStateListener(this.renderState)
    }

    async renderState() {
        const deckImage = /** @type {HTMLImageElement} */ (this
            .querySelector("div#membrane-overview img"))
        const dataUrl = await getMembraneBreakdownImageUrl(
            this.#calculator.bom,
            this.#deck
        )
        deckImage.src = dataUrl

        const membraneLayoutRows = []
        const membranes = this.#calculator.bom
            .filterDisplayCategory(PRODUCT_DISPLAY_CATEGORIES.membrane)
        for (const lineItem of membranes.lineItems) {
            const row = document.createElement("tr")

            const spacer = document.createElement("td")
            spacer.classList.add("__cell", "--spacer")

            const membraneDetail = document.createElement("td")
            membraneDetail.classList.add("__cell")
            membraneDetail.innerText = lineItem.detail?.id || ""

            const rollWidth = document.createElement("td")
            rollWidth.classList.add("__cell", "--center")
            rollWidth.innerText = `${lineItem.product.width.toFixed(2)}m`

            const rollLength = document.createElement("td")
            rollLength.classList.add("__cell", "--center")
            rollLength.innerText = `${lineItem.quantity.toFixed(2)}m`

            row.append(spacer, membraneDetail, rollWidth, rollLength)
            row.classList.add("__row")
            membraneLayoutRows.push(row)
        }

        this.querySelector("[data-accessor='membrane-layout']")
            ?.replaceChildren(...membraneLayoutRows)

        const upgradeConfig = (
            /** @type {SeamDirection} */ upgradeDirection
        ) => {
            const { seamDirection } = this.#calculator.config
            this.#calculator.config.seamDirection = upgradeDirection

            return () => {
                this.#calculator.config.seamDirection = seamDirection
            }
        }

        this.querySelectorAll("[data-accessor='seam-direction-selector'] ul li")
            .forEach(option => {
                const { bom: { price } } = this.#calculator
                const seamDirection = /** @type {any} */ (option).dataset.value

                const upgradeSpan = /** @type {HTMLSpanElement} */ (option
                    ?.querySelector("span.upgrade"))
                if (seamDirection === this.#calculator.config.seamDirection) {
                    upgradeSpan.innerText = "Selected"
                }
                else {
                    const resetConfig = upgradeConfig(seamDirection)

                    try {
                        this.#calculator.updateBom()
                        const upgradePrice = this.#calculator.bom.price
                        const deltaPrice = upgradePrice - price

                        const sign = deltaPrice < 0 ? "-" : "+"
                        upgradeSpan.innerText = deltaPrice === 0 ? "Same Price"
                            : `${sign} £${Math.abs(deltaPrice).toFixed(2)}`
                    }
                    catch {
                        option.classList.add("inactive")
                        upgradeSpan.innerText = "Unavailable"
                    }

                    // 
                    resetConfig()
                    this.#calculator.updateBom()
                }
            })
    }

    get #calculator() {
        return /** @type {EpdmCalculator} */ (App.state.quote.calculator)
    }

    get #deck() {
        return /** @type {Deck} */ (App.state.deck.client)
    }

    static get customName() {
        return "quote-upgrade-card"
    }
}

class ConfigUpgradeCard extends StyledComponent {
    /**
     * @param {object} obj Optional destructured parameters object.
     * @param {string} [obj.templateId]
     */
    constructor({ templateId } = {}) {
        // const customElementRegistry = new CustomElementRegistry()
        // customElementRegistry
        //     .define("collapsible-container", CollapsibleContainer)

        super({ templateId })
    }

    connectedCallback() {
        // 
        this.renderState()
        App.addStateListener(() => { this.renderState() })

        // 
        this.template?.addEventListener("click", () => {
            if (!this.selected) {
                this.upgradeConfig()
                App.dispatchStateUpdated()
            }
        })

        // 
        this.template
            ?.querySelector("button.toolbar")
            ?.addEventListener("click", event => {
                this.renderQuoteBreakdownModal(event)
            })
    }

    /**
     * Remove app state listener.
     */
    disconnectedCallback() {
        App.removeStateListener(this.renderState)
    }

    renderState() {
        // 
        if (this.template?.classList.contains("inactive")) { return }

        // 
        // const priceSpan = /** @type {HTMLSpanElement} */ (this.template
        //     ?.querySelector("span.price"))
        const upgradeSpan = /** @type {HTMLSpanElement} */ (this.template
            ?.querySelector("span.upgrade"))

        // 
        const { bom: { price } } = this.calculator
        if (this.selected) {
            this.template?.classList.add("selected")
            // priceSpan.innerText = `£${price.toFixed(2)}`
            upgradeSpan.innerText = "Selected"
            return
        }
        this.template?.classList.remove("selected")

        // 
        const resetConfig = this.upgradeConfig()

        // 
        try {
            this.calculator.updateBom()
            const { bom: { price: upgradePrice } } = this.calculator
            const deltaPrice = upgradePrice - price

            const sign = deltaPrice < 0 ? "-" : "+"
            // priceSpan.innerText = `£${upgradePrice.toFixed(2)}`
            upgradeSpan.innerText = deltaPrice === 0 ? "Same Price"
                : `${sign} £${Math.abs(deltaPrice).toFixed(2)}`
        }
        catch {
            this.template?.classList.add("inactive")
            upgradeSpan.innerText = "Unavailable"
            if (this.dataset.closeUnavailable === "true") {
                this.classList.add("closed")
            }
        }

        // 
        resetConfig()
        this.calculator.updateBom()
    }

    upgradeConfig() {
        return () => {}
    }

    /**
     * 
     * @param {Event} event 
     */
    renderQuoteBreakdownModal(event) {
        // 
        event.stopPropagation()

        const lineItems = this.calculator.bom.lineItems
        const resetConfig = this.upgradeConfig()
        this.calculator.updateBom()
        const updatedLineItems = this.calculator.bom.lineItems
            .sort((lineItemA, lineItemB) => {
                // Parse product display orders from line items.
                const { product: { displayOrder: orderA } } = lineItemA
                const { product: { displayOrder: orderB } } = lineItemB

                return orderA - orderB
            })

        // 
        const modal = SteppedContainer.modalContainer()
        const breakdown = cloneTemplate("quote-breakdown-modal")

        // 
        breakdown.querySelector("button.secondary")
            ?.addEventListener("click", () => { App.container.unloadModal() })
        breakdown.querySelector("button.primary")
            ?.addEventListener("click", () => {
                this.upgradeConfig()
                this.calculator.updateBom()
                App.dispatchStateUpdated()
                App.container.unloadModal()
            })

        breakdown.querySelector("[data-accessor='details']").innerText = this.querySelector("[data-accessor='description'").innerText

        const table = /** @type {HTMLTableSectionElement} */ (breakdown
            .querySelector("tbody"))
        for (const lineItem of lineItems) {
            if (!lineItem.quantity) { continue }

            const updatedLineItem = updatedLineItems.find(updatedLineItem => {
                return updatedLineItem.code === lineItem.code &&
                    updatedLineItem.quantity === lineItem.quantity
            })
            if (updatedLineItem) { continue }

            const name = document.createElement("td")
            name.classList.add("wrap")
            name.innerText = lineItem.product.name

            const quantity = document.createElement("td")
            quantity.innerText = `${lineItem.quantity}`

            const row = document.createElement("tr")
            row.append(name, quantity)
            row.style.textDecoration = "line-through"
            table.append(row)
        }
        for (const updatedLineItem of updatedLineItems) {
            if (!updatedLineItem.quantity) { continue }

            const lineItem = lineItems.find(lineItem => {
                return updatedLineItem.code === lineItem.code &&
                    updatedLineItem.quantity === lineItem.quantity
            })
            if (lineItem) { continue }

            const name = document.createElement("td")
            name.classList.add("wrap")
            name.innerText = updatedLineItem.product.name

            const quantity = document.createElement("td")
            quantity.innerText = `${updatedLineItem.quantity}`

            const row = document.createElement("tr")
            row.append(name, quantity)
            row.style.fontWeight = "bold"
            table.append(row)
        }

        // 
        if (!table.children.length) {
            const closeButton = breakdown.querySelector("button.secondary")
            const selectButton = breakdown.querySelector("button.primary")
            closeButton?.classList.replace("secondary", "primary")
            closeButton.innerText = "Close"
            selectButton?.classList.add("closed")

            breakdown.querySelector("table")?.remove()
            breakdown.querySelector("[data-accessor='review']")
                ?.classList.add("closed")
            breakdown.querySelector("[data-accessor='selected']")
                ?.classList.remove("closed")
        }

        // 
        resetConfig()
        this.calculator.updateBom()

        modal.append(breakdown)
        App.container.loadModal(modal)
    }

    get calculator() {
        return /** @type {EpdmCalculator} */ (App.state.quote.calculator)
    }

    get selected() {
        return false
    }

    static get customName() {
        return "config-upgrade-card"
    }
}

class MembraneUpgradeCard extends ConfigUpgradeCard {
    constructor() {
        super({ templateId: "membrane-upgrade-card" })
    }

    upgradeConfig() {
        const membrane = this.calculator.config.membrane
        const upgradeMembrane = Object
            .values(MEMBRANES)
            .find(membrane => membrane.id === this.dataset.membraneId)
        if (upgradeMembrane) {
            this.calculator.config.membrane = upgradeMembrane
        }

        return () => { this.calculator.config.membrane = membrane }
    }

    get selected() {
        return this.calculator.config.membrane.id === this.dataset.membraneId
    }

    static get customName() {
        return "membrane-upgrade-card"
    }
}

class TrimUpgradeCard extends ConfigUpgradeCard {
    constructor() {
        super({ templateId: "trim-upgrade-card" })
    }

    upgradeConfig() {
        const {
            color,
            type,
            minLength,
            maxLength
        } = this.calculator.config.trims
        this.calculator.config.trims.color = /** @type {TrimColor} */ (this
            .dataset.trimColor)
        this.calculator.config.trims.type = /** @type {TrimType} */ (this
            .dataset.trimType)
        this.calculator.config.trims.minLength = this.dataset.minLength
            ? parseFloat(this.dataset.minLength) : 0
        this.calculator.config.trims.maxLength = this.dataset.maxLength
            ? parseFloat(this.dataset.maxLength) : Infinity

        return () => {
            this.calculator.config.trims.color = color
            this.calculator.config.trims.type = type
            this.calculator.config.trims.minLength = minLength
            this.calculator.config.trims.maxLength = maxLength
        }
    }

    get selected() {
        return this.calculator.config.trims.color === this.dataset.trimColor &&
            this.calculator.config.trims.type === this.dataset.trimType &&
            this.calculator.config.trims.minLength === (this.dataset.minLength
                ? parseFloat(this.dataset.minLength) : 0) &&
            this.calculator.config.trims.maxLength === (this.dataset.maxLength
                ? parseFloat(this.dataset.maxLength) : Infinity)
    }

    static get customName() {
        return "trim-upgrade-card"
    }
}

// @@exports
export { QuoteUpgradeCard, MembraneUpgradeCard, TrimUpgradeCard }
